import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { soccerPath } from '../utils/paths';
import './TeamEdit.css';

const TeamEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    memberIds: [],
    numberOfTeams: 2,
    maxPlayersPerTeam: null,
  });
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [teamData, playersData] = await Promise.all([
        api.getTeamById(id, user?.id),
        api.getAllPlayers(user?.id),
      ]);
      
      setTeam(teamData);
      setPlayers(playersData);
      setFormData({
        name: teamData.name,
        description: teamData.description || '',
        memberIds: teamData.members?.map(m => m.id) || [],
        numberOfTeams: teamData.numberOfTeams || 2,
        maxPlayersPerTeam: teamData.maxPlayers || null,
      });
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'numberOfTeams' || name === 'maxPlayersPerTeam' 
        ? (value ? parseInt(value) : null) 
        : value,
    }));
  };

  const handleMemberToggle = (playerId) => {
    setFormData((prev) => ({
      ...prev,
      memberIds: prev.memberIds.includes(playerId)
        ? prev.memberIds.filter((id) => id !== playerId)
        : [...prev.memberIds, playerId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user && !isAdmin()) {
      setError('You must be logged in to edit teams');
      return;
    }

    if (formData.memberIds.length < formData.numberOfTeams) {
      setError(`You need at least ${formData.numberOfTeams} players to divide into ${formData.numberOfTeams} teams`);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        memberIds: formData.memberIds,
        numberOfTeams: formData.numberOfTeams,
        maxPlayersPerTeam: formData.maxPlayersPerTeam,
      };

      await api.updateTeam(id, updateData, user?.id || null, isAdmin());
      setSuccess('Team updated successfully!');
      
      setTimeout(() => {
        navigate(soccerPath(`/teams/${id}`));
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading team data...</div>;
  }

  if (!team) {
    return <div className="alert alert-error">Team not found</div>;
  }

  return (
    <div className="card">
      <h2>Edit Team: {team.name}</h2>
      
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {user && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          Logged in as: <strong>{user.name}</strong> ({user.roles && Array.isArray(user.roles) ? user.roles.join(', ') : 'N/A'})
          {isAdmin() && <span style={{ marginLeft: '1rem', color: '#155724' }}>✓ Admin Access</span>}
          {user.isApprovedCreator && <span style={{ marginLeft: '1rem', color: '#155724' }}>✓ Approved Creator</span>}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Team Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <input
            type="text"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>
            Select Team Members ({formData.memberIds.length} selected)
          </label>
          <div className="checkbox-group">
            {players.map((player) => (
              <div
                key={player.id}
                className="checkbox-item"
              >
                <input
                  type="checkbox"
                  id={`player-${player.id}`}
                  checked={formData.memberIds.includes(player.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleMemberToggle(player.id);
                  }}
                />
                <label htmlFor={`player-${player.id}`}>
                  <strong>{player.name}</strong>
                  <br />
                  <small>
                    {player.position}
                    {user && (isAdmin() || (user.roles && user.roles.includes('TEAM_CREATOR'))) && ` • Skill: ${player.skillLevel}/10`}
                  </small>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="numberOfTeams">Number of Teams *</label>
          <input
            type="number"
            id="numberOfTeams"
            name="numberOfTeams"
            min="2"
            max="10"
            value={formData.numberOfTeams}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="maxPlayersPerTeam">Max Players Per Team</label>
          <input
            type="number"
            id="maxPlayersPerTeam"
            name="maxPlayersPerTeam"
            min="1"
            value={formData.maxPlayersPerTeam || ''}
            onChange={handleChange}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Updating...' : 'Update Team'}
          </button>
          <button
            type="button"
            onClick={() => navigate(soccerPath(`/teams/${id}`))}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamEdit;

